<template>
  <v-sheet color="surface" rounded="lg" class="pa-4 fax-editor">
    <v-container :fluid="true">
      <v-container class="pa-0">
        <v-row>
          <v-col>
            <div v-if="props.title" class="fax-editor__title">{{ props.title }}</div>
            <div v-if="errorMsg" class="text-error">{{ errorMsg }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-combobox
              v-model="toFaxNumbers"
              label="To Fax Number"
              multiple
              chips
              closable-chips
              :rules="[faxNumbersRule]"
              hint="Press Enter to add multiple fax numbers"
              persistent-hint />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="templateItems.length > 0">
            <v-combobox
              :items="templateItems"
              label="Select a fax template"
              :model-value="selectedTemplate"
              @update:model-value="onTemplateSelected" />
          </v-col>
        </v-row>
        <v-row v-if="props.attachmentOptions.length > 0">
          <v-col>
            <v-combobox
              :items="props.attachmentOptions"
              label="Select an attachment"
              :model-value="selectedAttachment"
              clearable
              @update:model-value="onAttachmentSelected" />
            <v-checkbox
              v-if="supportingDocumentsUrls.length > 0"
              v-model="includeSupportingDocuments"
              label="Include supporting documents"
              hide-details
              class="mt-2" />
            <div
              v-if="includeSupportingDocuments && supportingDocumentsUrls.length > 0 && selectedAttachment"
              class="text-caption text-medium-emphasis mt-1">
              {{ getAttachmentSummary() }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea v-model="content" label="Content" @update:model-value="(v) => emit('update:content', v)" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="props.cancelButton" cols="4">
            <v-btn variant="outlined" color="primary" rounded :loading="loading" @click="onCancel">Cancel</v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <slot name="attach"></slot>
          <v-col v-if="!props.appeal" cols="auto" align="right">
            <v-btn
              v-if="downloadLinks.length > 0"
              variant="tonal"
              color="primary"
              rounded
              prepend-icon="mdi-paperclip"
              :disabled="true"
              :loading="loading">
              Attached
            </v-btn>
            <template v-else>
              <v-btn
                v-if="!showFileUpload"
                variant="tonal"
                color="primary"
                rounded
                prepend-icon="mdi-paperclip"
                :loading="loading"
                @click="onAttachFile">
                Attach a File
              </v-btn>
              <UploadFileComponent v-if="showFileUpload" @download-link="onDownloadLink" />
            </template>
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn
              variant="tonal"
              color="primary"
              rounded
              prepend-icon="mdi-eye-outline"
              :disabled="(!content && !selectedAttachment) || !toFaxNumbers.length"
              :loading="loading"
              @click="onPreview">
              Preview
            </v-btn>
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn
              color="primary"
              variant="flat"
              rounded
              prepend-icon="mdi-send-outline"
              :disabled="(!content && !selectedAttachment) || !toFaxNumbers.length"
              :loading="loading"
              @click="onSend">
              {{ props.sendButtonText }}
            </v-btn>
          </v-col>
        </v-row>
        <v-dialog v-model="showPreview" width="90%" height="90%">
          <v-card>
            <v-card-title>Preview</v-card-title>
            <v-card-subtitle> Send to: {{ toFaxNumbers.join(", ") }} </v-card-subtitle>
            <v-card-text>
              <div v-if="loading" class="d-flex flex-column justify-center align-center" style="height: 70vh">
                <v-progress-circular indeterminate color="primary" class="mb-4" />
                <div
                  v-if="selectedAttachment?.value?.type === 'flattened_form'"
                  class="text-body-2 text-medium-emphasis">
                  Rendering the filled form with signature and checkboxes — this can take a moment for large forms.
                </div>
              </div>
              <iframe v-else-if="previewUrl" :src="previewUrl" width="100%" style="height: 70vh; border: none"></iframe>
              <iframe v-else :srcdoc="previewContent" width="100%" height="100%" frameborder="0"></iframe>
            </v-card-text>
            <v-card-actions>
              <v-btn-lh @click="showPreview = false">Close</v-btn-lh>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { templateService } from "@/services/templateService";
import { eFaxService } from "@/services/eFaxService";
import { IntakeOrder, IntakeDocument } from "@/models/intake";
import { ClientTemplate } from "@/models/templates";
import { isPhoneNumberValid } from "@/utils/validators";
import UploadFileComponent from "@/components/UploadFileComponent.vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  types: {
    type: Array,
    required: false,
    default: () => [],
  },
  toFaxNumber: {
    type: String,
    required: false,
    default: null,
  },
  intakeOrder: {
    type: IntakeOrder,
    required: false,
    default: null,
  },
  intakeDocument: {
    type: IntakeDocument,
    required: false,
    default: null,
  },
  orderInfo: {
    type: Object,
    required: false,
    default: null,
  },
  cancelButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  sendButtonText: {
    type: String,
    required: false,
    default: "Send Fax",
  },
  deferSend: {
    type: Boolean,
    required: false,
    default: false,
  },
  attachmentOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
  preselectedAttachment: {
    type: Object,
    required: false,
    default: null,
  },
  prefilledContent: {
    type: String,
    required: false,
    default: null,
  },
  frontendTemplateName: {
    type: String,
    required: false,
    default: null,
  },
  frontendTemplate: {
    type: Object,
    required: false,
    default: null,
  },
  supportingDocuments: {
    type: Array,
    required: false,
    default: () => [],
  },
  appeal: {
    type: Boolean,
    required: false,
    default: false,
  },
  attachSupportingDocsFaxEnabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  extraFileLinks: {
    type: Array,
    required: false,
    default: () => [],
  },
  excludedEnclosureLinks: {
    type: Array,
    required: false,
    default: () => [],
  },
});
const emit = defineEmits(["onComplete", "onPreSend", "onFrontendTemplateSelected", "update:content"]);

const templates = ref([]);
const templateItems = ref([]);
const showFileUpload = ref(false);
const selectedTemplate = ref(null);
const content = ref("");
const errorMsg = ref("");
const loading = ref(false);
const toFaxNumbers = ref([]);
const downloadLinks = ref([]);

const faxNumbersRule = (v) => {
  if (!v || v.length === 0) return "At least one fax number is required";
  const invalid = v.find((num) => !isPhoneNumberValid(num));
  return invalid ? `"${invalid}" is not a valid fax number (10 digits)` : true;
};
const showPreview = ref(false);
const previewContent = ref("");
const previewUrl = ref("");
const selectedAttachment = ref(props.preselectedAttachment || null);
const includeSupportingDocuments = ref(props.attachSupportingDocsFaxEnabled || false);

const supportingDocumentsUrls = computed(() => {
  return props.supportingDocuments || [];
});

onMounted(() => {
  if (props.toFaxNumber) {
    toFaxNumbers.value = [props.toFaxNumber];
  }
  if (props.types && props.types.length > 0) {
    fetchTemplates();
  }
});

watch(
  () => props.types,
  () => {
    if (props.types && props.types.length > 0 && !props.prefilledContent) {
      fetchTemplates();
    }
  },
  { immediate: false }
);

const onTemplateSelected = (selected) => {
  selectedTemplate.value = selected;
  // For frontend templates, set content and emit event
  if (selected.value === "frontend-template" && props.frontendTemplate) {
    content.value = props.frontendTemplate.content || "";
    emit("onFrontendTemplateSelected", props.frontendTemplate);
    return;
  }
  const template = templates.value.find((t) => t.id === selected.value);
  content.value = template?.editableContent ?? "";
};

watch(
  () => props.frontendTemplate,
  (newTemplate) => {
    if (newTemplate) {
      // Add or update frontend template in the list
      const existingIndex = templateItems.value.findIndex((item) => item.value === "frontend-template");
      const frontendTemplateItem = {
        title: newTemplate.name,
        value: "frontend-template",
      };

      if (existingIndex >= 0) {
        templateItems.value[existingIndex] = frontendTemplateItem;
      } else {
        templateItems.value.unshift(frontendTemplateItem);
      }

      // Auto-select frontend template when it's provided
      selectedTemplate.value = frontendTemplateItem;
      onTemplateSelected(frontendTemplateItem);
    } else {
      // Remove frontend template from list if it's no longer provided
      const existingIndex = templateItems.value.findIndex((item) => item.value === "frontend-template");
      if (existingIndex >= 0) {
        templateItems.value.splice(existingIndex, 1);
        // If it was selected, clear selection
        if (selectedTemplate.value?.value === "frontend-template") {
          selectedTemplate.value = templateItems.value.length > 0 ? templateItems.value[0] : null;
          content.value = "";
        }
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.toFaxNumber,
  (newValue) => {
    if (newValue !== undefined && newValue !== null) {
      // Keep primary number (index 0) in sync with the prop; preserve any additional numbers
      toFaxNumbers.value = [newValue, ...toFaxNumbers.value.slice(1)];
    }
  },
  { immediate: false }
);

watch(
  () => props.preselectedAttachment,
  (newValue) => {
    if (newValue) {
      selectedAttachment.value = newValue;
    }
  },
  { immediate: true }
);

const fetchTemplates = async () => {
  try {
    loading.value = true;
    errorMsg.value = "";
    const response = await templateService.getTemplates({
      templateTypes: props.types,
      intakeOrderId: props.intakeOrder?.id,
      orderInfoKey: props.orderInfo?.orderKey,
      intakeDocumentKey: props.intakeDocument?.documentKey,
    });
    templates.value = response.results.map((t) => new ClientTemplate(t));
    // Preserve frontend template if it exists
    const frontendTemplateItem = templateItems.value.find((item) => item.value === "frontend-template");

    // Add backend templates
    const backendTemplateItems = templates.value.map((t) => ({
      title: t.name ?? t.templateType,
      value: t.id,
    }));

    // Combine: frontend template first (if exists), then backend templates
    templateItems.value = frontendTemplateItem ? [frontendTemplateItem, ...backendTemplateItems] : backendTemplateItems;

    // Select frontend template if it exists, otherwise select first backend template
    if (frontendTemplateItem) {
      selectedTemplate.value = frontendTemplateItem;
      onTemplateSelected(frontendTemplateItem);
    } else if (templateItems.value.length > 0) {
      selectedTemplate.value = templateItems.value[0];
      onTemplateSelected(selectedTemplate.value);
    }
  } catch (error) {
    errorMsg.value = `Error fetching templates: ${error.message}`;
  } finally {
    loading.value = false;
  }
};

const onAttachFile = () => {
  showFileUpload.value = true;
};

const onDownloadLink = (download) => {
  downloadLinks.value.push(download.link);
};

const onAttachmentSelected = (selected) => {
  // Allow clearing selection (null or undefined)
  selectedAttachment.value = selected || null;
};

const getAttachmentSummary = () => {
  if (!selectedAttachment.value || supportingDocumentsUrls.value.length === 0) {
    return "";
  }
  const attachmentName = selectedAttachment.value.title || "Filled Form";
  const docCount = supportingDocumentsUrls.value.length;
  return `${attachmentName} + ${docCount} supporting document${docCount !== 1 ? "s" : ""}`;
};

const resetForm = () => {
  content.value = "";
  selectedAttachment.value = null;
  downloadLinks.value = [];
  showPreview.value = false;
  previewContent.value = "";
  previewUrl.value = "";
  errorMsg.value = "";
};

// Shared by onSend and onPreview's flattened-form path so a field added to one is never
// silently missing from the other (see PR history — text/templateId/fileLinks drifted out
// of the preview this exact way before).
const buildAttachmentData = () => {
  return selectedAttachment.value?.value
    ? {
        type: selectedAttachment.value.value.type,
        value: selectedAttachment.value.value.type === "response_fax" ? selectedAttachment.value.value.s3_url : null,
      }
    : null;
};

const buildFaxRequestData = (attachmentData, { previewOnly = false } = {}) => {
  const fileLinks = [...downloadLinks.value];
  if (includeSupportingDocuments.value && supportingDocumentsUrls.value.length > 0) {
    fileLinks.push(...supportingDocumentsUrls.value);
  }

  const requestData = {
    text: content.value,
    toFaxNumbers: toFaxNumbers.value,
    fileLinks: fileLinks,
  };
  if (selectedTemplate.value?.value && selectedTemplate.value.value !== "frontend-template") {
    requestData.templateId = selectedTemplate.value.value;
  }
  if (attachmentData) {
    requestData.attachment = attachmentData;
  }
  if (props.intakeOrder) {
    requestData.intakeOrderId = props.intakeOrder.id;
  }
  if (props.orderInfo) {
    requestData.orderInfoKey = props.orderInfo.orderKey;
  }
  if (props.intakeDocument) {
    requestData.intakeDocumentKey = props.intakeDocument.documentKey;
  }

  if (previewOnly) {
    requestData.previewOnly = true;
    return requestData;
  }

  if (props.appeal) {
    requestData.appeal = true;
  }
  if (props.extraFileLinks.length > 0) {
    requestData.appealAttachmentLinks = props.extraFileLinks;
  }
  if (props.excludedEnclosureLinks.length > 0) {
    requestData.excludedEnclosureLinks = props.excludedEnclosureLinks;
  }
  return requestData;
};

const onSend = async () => {
  try {
    errorMsg.value = "";
    loading.value = true;

    const attachmentData = buildAttachmentData();
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";

    if (isFlattenedFormAttachment) {
      try {
        await new Promise((resolve, reject) => {
          emit("onPreSend", resolve, reject);
        });
      } catch (saveError) {
        errorMsg.value = "Failed to save prior authorization form before faxing. Please try again.";
        loading.value = false;
        return;
      }
    }

    const requestData = buildFaxRequestData(attachmentData);
    if (props.deferSend) {
      const isFrontendTemplate = selectedTemplate.value?.value === "frontend-template";
      const frontendTemplateCategory =
        isFrontendTemplate && props.frontendTemplate ? props.frontendTemplate.category : null;
      emit("onComplete", { sent: true, queuePayload: requestData, isFrontendTemplate, frontendTemplateCategory });
      resetForm();
      return;
    }
    const response = await eFaxService.postSendOutgoingFax(requestData);
    const isFrontendTemplate = selectedTemplate.value?.value === "frontend-template";
    const frontendTemplateCategory =
      isFrontendTemplate && props.frontendTemplate ? props.frontendTemplate.category : null;
    emit("onComplete", { sent: response, isFrontendTemplate, frontendTemplateCategory });
    // Clear form after successful send
    resetForm();
  } catch (error) {
    if (error instanceof Error && error.format) {
      errorMsg.value = error.format();
    } else {
      errorMsg.value = `Error sending fax: ${error.message || "Unknown error"}`;
    }
  } finally {
    loading.value = false;
  }
};

defineExpose({});

const onCancel = () => {
  emit("onComplete", { sent: null });
};

const onPreview = async () => {
  showPreview.value = true;
  loading.value = true;
  previewUrl.value = "";
  previewContent.value = "";
  try {
    // Appeals: preview the exact merged fax the backend would send (letter + auto-attached order
    // enclosures + manual attachments), not just the letter HTML — so preview matches what sends.
    if (props.appeal) {
      // Preview merges every enclosure PDF from S3 synchronously on the backend (no Celery task),
      // which can exceed the default request timeout — give it more room than a normal API call.
      const response = await eFaxService.postSendOutgoingFax(
        {
          text: content.value || "",
          toFaxNumbers: toFaxNumbers.value,
          orderInfoKey: props.orderInfo?.orderKey,
          appeal: true,
          appealAttachmentLinks: props.extraFileLinks,
          excludedEnclosureLinks: props.excludedEnclosureLinks,
          previewOnly: true,
        },
        { timeout: 120000 }
      );
      previewUrl.value = response?.previewUrl || "";
      return;
    }
    const attachmentData = buildAttachmentData();
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";

    // The flattened form is rendered from the order's saved state (signature, checkboxes, filled
    // fields), so save current edits first — or the preview would show stale data. Pass
    // previewOnly so the listener (saveOrderDraft) persists the draft without also creating an
    // order or advancing formStep — those are send-only side effects a preview must not trigger.
    if (isFlattenedFormAttachment) {
      try {
        await new Promise((resolve, reject) => {
          emit("onPreSend", resolve, reject, { previewOnly: true });
        });
      } catch (saveError) {
        errorMsg.value = "Failed to save prior authorization form before rendering the preview. Please try again.";
        showPreview.value = false;
        return;
      }
      // Built via buildFaxRequestData so the preview is assembled from the exact same
      // content/attachments/fileLinks logic as onSend — otherwise a field added to onSend
      // silently drifts out of the preview again.
      const previewRequestData = buildFaxRequestData(attachmentData, { previewOnly: true });
      // Preview flattens/merges the form PDF and uploads to S3 synchronously on the backend (no
      // Celery task), which can exceed the default request timeout — give it more room.
      const response = await eFaxService.postSendOutgoingFax(previewRequestData, { timeout: 120000 });
      previewUrl.value = response?.previewUrl || "";
      return;
    }
    const shouldPreviewAttachment = attachmentData && !isFlattenedFormAttachment;

    const baseRequestData = {
      content: content.value || "",
    };
    if (props.orderInfo?.orderKey) {
      baseRequestData.orderInfoKey = props.orderInfo.orderKey;
    }
    if (props.intakeOrder?.id) {
      baseRequestData.intakeOrderId = props.intakeOrder.id;
    }
    if (props.intakeDocument?.documentKey) {
      baseRequestData.intakeDocumentKey = props.intakeDocument.documentKey;
    }

    let response = "";

    // Handle frontend templates (no backend template)
    if (selectedTemplate.value?.value === "frontend-template") {
      if (shouldPreviewAttachment) {
        const requestPayload = { ...baseRequestData, attachment: attachmentData };
        response = await templateService.getFaxContent(requestPayload);
      } else if (baseRequestData.content) {
        response = await templateService.getFaxContent(baseRequestData);
      }
    } else if (selectedTemplate.value) {
      if (shouldPreviewAttachment) {
        const requestPayload = {
          ...baseRequestData,
          attachment: attachmentData,
          template_id: selectedTemplate.value.value,
        };
        response = await templateService.getFaxContent(requestPayload);
      } else {
        response = await templateService.getTemplatePreview(selectedTemplate.value.value, content.value || "");
      }
    } else if (shouldPreviewAttachment) {
      const requestPayload = { ...baseRequestData, attachment: attachmentData };
      response = await templateService.getFaxContent(requestPayload);
    } else if (baseRequestData.content) {
      response = await templateService.getFaxContent(baseRequestData);
    }

    if (!response) {
      response = "<p style='font-family: DM Sans; color: #5E7898;'>No additional content selected for preview.</p>";
    }

    previewContent.value = response;
  } catch (error) {
    errorMsg.value = `Error fetching preview: ${error.message}`;
    showPreview.value = false;
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.fax-editor {
  border: 1px solid $lh_dash_border;
  box-shadow: $lh_shadow_sm;
  border-radius: 16px !important;

  :deep(.v-btn) {
    letter-spacing: normal;
    text-transform: none;
    font-weight: 500;
  }
}

.fax-editor__title {
  font-family: $lh_font_family_dm_sans;
  font-size: 17px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: $lh_color_navy_dark;
}
</style>
